import React, { ForwardRefRenderFunction, forwardRef, useImperativeHandle, useState } from "react";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import { Color } from "../../../../components/Form/Button";
import Input from "../../../../components/Form/Input";
import { useAppState } from "../../../../context/StateContext";
import useYupValidation from "../../../../hooks/useYupValidation";
import { CustomerPortalFooter } from "../../../private/Moving/CustomerPortal/CustomerPortalFooter";
import { validateForm } from "../../ForgotPassword/actions";
import useStyles, {
  ContainerFooter,
  ForgotFormWrapper,
  GoBackToLoginButton,
  LoginForm,
  LoginInfo,
  LoginSubtitle,
  LoginTitle,
  LogoImage,
  PageWrapper,
  ResendContainer,
  TextLink,
  ResendText,
  ResetPasswordButton,
} from "./styles";

interface ForgotPasswordFormProps {
  handleSubmit?: (event: React.FormEvent, tokenIsExpired?: boolean) => void;
  emailSent?: boolean;
  isLoading?: boolean;
}

export interface ForgotPasswordFormHandle {
  email: string;
}

const ForgotPasswordForm: ForwardRefRenderFunction<
  ForgotPasswordFormHandle,
  ForgotPasswordFormProps
> = (props, ref) => {
  const { emailSent, handleSubmit, isLoading } = props;

  const classes = useStyles();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [email, setEmail] = useState<string>("");

  const { subDomainInfo } = useAppState();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const { t: translate } = useTranslation();
  const history = useHistory();

  const handleSubmitForm = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm(translate, email, setErrors)) {
      if (handleSubmit) handleSubmit(event);
    }
  };

  useImperativeHandle(ref, () => ({
    email,
  }));

  const renderContent = () => {
    if (emailSent) {
      return (
        <LoginForm onSubmit={handleSubmitForm}>
          <LogoImage src={subDomainInfo?.logo} />

          <LoginInfo>
            <LoginTitle>{translate("checkYourEmail")}</LoginTitle>
            <LoginSubtitle>
              {translate("ifAMatchingAccountWasFoundAnEmailWasSentTo")}
              <b>{` ${email} `}</b>
              {translate("withInstructionsToResetYourPassword")}
            </LoginSubtitle>
          </LoginInfo>

          <ForgotFormWrapper>
            <GoBackToLoginButton
              fullWidth
              customColor={Color.SECONDARY}
              loading={isLoading}
              onClick={() => history.push("/")}
              data-cy="goBackToLoginButton"
              aria-label="goBackToLoginButton"
            >
              {translate("goBackToLogin")}
            </GoBackToLoginButton>

            <ResendContainer>
              <ResendText>{translate("emailNotReceived")}</ResendText>
              <TextLink onClick={handleSubmitForm}>{translate("resend")}</TextLink>
            </ResendContainer>
          </ForgotFormWrapper>
        </LoginForm>
      );
    }

    return (
      <LoginForm onSubmit={handleSubmitForm}>
        <LogoImage src={subDomainInfo?.logo} />

        <LoginInfo>
          <LoginTitle>{translate("forgotYourPassword")}</LoginTitle>
          <LoginSubtitle>
            {translate("dontWorryJustEnterYourEmailAddressBelowAndWellAssistYou")}
          </LoginSubtitle>
        </LoginInfo>

        <Input
          fullWidth
          inputProps={{
            "aria-label": "user-mail",
          }}
          label={translate("email")}
          labelClassName={classes.label}
          value={email}
          disabled={isLoading}
          placeholder={translate("typeHere")}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
          error={fieldHasError("email")}
          errorMessage={getErrorByField("email")}
        />

        <ForgotFormWrapper>
          <ResetPasswordButton
            type="submit"
            fullWidth
            loading={isLoading}
            data-cy="resetPasswordButton"
            aria-label="resetPasswordButton"
          >
            {translate("resetPassword")}
          </ResetPasswordButton>

          <TextLink onClick={() => history.push("/")}>{translate("returnToLogin")}</TextLink>
        </ForgotFormWrapper>
      </LoginForm>
    );
  };

  return (
    <PageWrapper>
      {renderContent()}
      <ContainerFooter>
        <CustomerPortalFooter center />
      </ContainerFooter>
    </PageWrapper>
  );
};

export default forwardRef(ForgotPasswordForm);
